/**
 * WMS mini-map helpers: EPSG:3857 bbox from field polygon, pixel size, GeoServer style name.
 */

import {PolygonConverterUtil} from '@app/utils/polygon-converter.util';

/** Web Mercator X/Y from WGS84 lon/lat (meters). */
export function lonLatTo3857(lon: number, lat: number): [number, number] {
  const R = 6378137;
  const x = ((lon * Math.PI) / 180) * R;
  let y = Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI) / 360));
  y = (y * R) / 1;
  return [x, y];
}

/** BBOX string for WMS GetMap in EPSG:3857, padded around the field parcel. */
export function computeBBox3857FromPolygonJson(polygonJson: string | null): string | null {
  if (!polygonJson) {
    return null;
  }
  const coords = PolygonConverterUtil.polygonJsonToCoordinates(polygonJson);
  if (!coords.length) {
    return null;
  }
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const [lat, lng] of coords) {
    const [x, y] = lonLatTo3857(lng, lat);
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x);
    maxY = Math.max(maxY, y);
  }
  const dx = maxX - minX || 1;
  const dy = maxY - minY || 1;
  const padX = dx * 0.08;
  const padY = dy * 0.08;
  return `${minX - padX},${minY - padY},${maxX + padX},${maxY + padY}`;
}

/**
 * WIDTH/HEIGHT for GetMap: capped dimensions matching bbox aspect ratio (avoids stretched tiles).
 */
export function computeWmsMapPixelSize(bboxStr: string, maxDim = 480): { w: number; h: number } {
  const parts = bboxStr.split(',').map(s => Number.parseFloat(s.trim()));
  if (parts.length !== 4 || parts.some(n => !Number.isFinite(n))) {
    return { w: maxDim, h: Math.round(maxDim * 0.75) };
  }
  const [minX, minY, maxX, maxY] = parts;
  let dx = maxX - minX;
  let dy = maxY - minY;
  if (dx <= 0) {
    dx = 1;
  }
  if (dy <= 0) {
    dy = 1;
  }
  const aspect = dx / dy;
  let w: number;
  let h: number;
  if (aspect >= 1) {
    w = maxDim;
    h = Math.max(64, Math.round(maxDim / aspect));
  } else {
    h = maxDim;
    w = Math.max(64, Math.round(maxDim * aspect));
  }
  w = Math.min(1024, Math.max(128, w));
  h = Math.min(1024, Math.max(128, h));
  return { w, h };
}

/** GeoServer SLD style name for the selected vegetation index (e.g. NDVI_int8). */
export function wmsStyleForIndex(indexValue: string): string {
  const k = String(indexValue || 'Ndvi').trim().toUpperCase();
  return `${k}_int8`;
}
